import { helpers, useFavorites } from '@grupoboticario/barril-del-chavo'
import { CSS, Heading, Text, IconButton, Spinner, Tag } from '@grupoboticario/flora-react'

import { formatCurrencyBR } from 'src/helpers'

import * as Styled from '../Styles'
import { ChartDownIcon, HeartFillIcon, HeartOutlineIcon } from '@grupoboticario/flora-react-icons'
import { FC, useEffect, useState, useMemo } from 'react'
import { IProduct } from 'src/@interfaces'
import { ProductTagService } from 'src/service'

const { hasDiscount } = helpers

export type ProductTagsProps = {
  tags: string[]
}

export const LowStockTag = ({ tags }: ProductTagsProps) => {
  const filteredTags = useMemo(
    () => tags.filter((tag) => ProductTagService.isLowStock(tag)),
    [tags],
  )

  return filteredTags.length > 0 ? (
    <Styled.Tags>
      {filteredTags.map((label: string) => (
        <Tag
          key={label}
          variant={'alert'}
          leftIcon={<ChartDownIcon />}
          size="small"
          css={{
            marginRight: '$1',
            marginBottom: '$1',
          }}
        >
          {label}
        </Tag>
      ))}
    </Styled.Tags>
  ) : null
}

const DiscountTag = ({ product }: { product: IProduct }) => {
  const discountPercentage = Math.round((product.discount / product.price) * 100)

  return (
    <Tag
      variant="primary"
      size="small"
      css={{
        marginLeft: '$1',
      }}
    >
      -{discountPercentage}%
    </Tag>
  )
}

interface IFavoritesToggleVariants {
  topLeftCorner: CSS
  topRightCorner: CSS
  relative: CSS
}

export interface FavoritesToggleProps {
  product: IProduct
  variant?: keyof IFavoritesToggleVariants
  css?: CSS
}

export type ProductPriceProps = {
  product: IProduct
}

export const ProductPrice = ({ product }: ProductPriceProps) => (
  <Styled.Price>
    <Text
      data-testid="ProductCard-discount"
      size="exceptionsRestrictedMedium"
      color="$nonInteractivePredominant"
      weight="medium"
    >
      {hasDiscount(product) && product.discount > 0 ? (
        <>
          De <Styled.DiscountPrice>{formatCurrencyBR(product.price)}</Styled.DiscountPrice> por
        </>
      ) : (
        <>Por</>
      )}
    </Text>

    <Heading
      level="6"
      color="$nonInteractivePredominant"
      css={{ fontWeight: '$medium' }}
    >
      {formatCurrencyBR(product.salePrice)}
      {hasDiscount(product) && product.discount > 0 ? <DiscountTag product={product} /> : null}
    </Heading>
  </Styled.Price>
)

export const FavoritesToggle: FC<FavoritesToggleProps> = ({ product, css = {} }) => {
  const [loading, setLoading] = useState(false)
  const [isFavoritesFull, setIsFavoritesFull] = useState(false)
  const [isItemInFavorites, setIsItemInFavorites] = useState(false)
  const favorites = useFavorites()

  const label = !isItemInFavorites ? 'Salvar como Favorito' : 'Remover dos Favoritos'

  const handleToggleItem = async (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
    const { sku, organization } = product

    try {
      setLoading(true)
      await favorites.toggleFavorite({ sku, organization: organization.slugName })
    } catch {
      /** do nothing */
    } finally {
      setLoading(false)
    }

    const itemInFavorites = await favorites.isFavorited(product.sku)
    setIsItemInFavorites(itemInFavorites)
  }

  useEffect(() => {
    favorites.isFull().then(setIsFavoritesFull)
    favorites.isFavorited(product.sku).then(setIsItemInFavorites)
  }, [product.sku, favorites])

  if (product.isKit || !favorites.isFavoritesEnabled) {
    return null
  }

  return loading ? (
    <Spinner
      color="$nonInteractiveAuxiliar"
      css={{
        position: 'absolute',
        top: '0',
        right: '0',
        margin: '$2',
        height: '2rem',
        width: '2rem',
        ...css,
      }}
    />
  ) : (
    <IconButton
      data-autotagging-flora-section="wishlist"
      autotagging-flora="on"
      aria-label={label}
      size="small"
      shape="pill"
      variant="ghost"
      onClick={handleToggleItem}
      disabled={isFavoritesFull && !isItemInFavorites}
      css={{
        boxShadow: 'none',
        backgroundColor: '$backgroundSecondary',
        position: 'absolute',
        top: '0',
        right: '0',
        ...css,
      }}
    >
      {isItemInFavorites ? <HeartFillIcon /> : <HeartOutlineIcon />}
    </IconButton>
  )
}
